/**
 * featureFlags.ts — shared cache for GET /api/public/features.
 *
 * Backs BOTH the `useAiFeaturesEnabled` hook (React, `aiTutor`) and the plain
 * async `isContentFanoutEnabled` getter (non-React call sites — courseJourney.ts's
 * create functions — `contentFanout`) off the SAME localStorage entry, so one
 * fetch answers both flags and a component mount can never disagree with a
 * concurrent non-hook call within the same TTL window.
 *
 * Cache key bumped v1 -> v2 when this module generalized from AI-tutor-only
 * (`{aiTutor, t}`) to both flags (`{aiTutor, contentFanout, t}`): a v1 entry
 * simply lives under a different, now-unread key, so a flag flip at deploy can
 * never be masked by a stale narrower-shaped entry surviving under the new
 * key — no migration needed, the old entry is just abandoned.
 *
 * Fail-closed throughout: an unreleased / mid-rollout feature must never
 * flash into view, and a content-create call must never guess a flag is on
 * when the fetch failed, so any error here resolves every flag to `false`
 * rather than throwing or leaving a stale value in place.
 */
import { api } from '../api';

export const FEATURES_CACHE_KEY = 'features:v2';
const CACHE_TTL_MS = 10 * 60 * 1000;

export interface FeatureFlags {
  aiTutor: boolean;
  contentFanout: boolean;
}

interface CacheEntry extends FeatureFlags {
  t: number;
}

const FAIL_CLOSED: FeatureFlags = { aiTutor: false, contentFanout: false };

function readCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(FEATURES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CacheEntry>;
    if (typeof parsed.t !== 'number' || Date.now() - parsed.t > CACHE_TTL_MS) return null;
    // Per-field coercion (not a whole-entry reject) so a cache entry is only
    // ever as stale/wrong as the fields it actually carries — matches the
    // fail-closed default for anything missing or non-boolean.
    return {
      aiTutor: parsed.aiTutor === true,
      contentFanout: parsed.contentFanout === true,
      t: parsed.t,
    };
  } catch {
    return null; // corrupt entry / storage unavailable (e.g. private mode)
  }
}

function writeCache(flags: FeatureFlags): void {
  try {
    localStorage.setItem(FEATURES_CACHE_KEY, JSON.stringify({ ...flags, t: Date.now() }));
  } catch {
    // Best-effort only.
  }
}

/** Read the cached flags synchronously. `null` on miss/expiry/corruption. */
export function readCachedFlags(): FeatureFlags | null {
  const entry = readCache();
  return entry ? { aiTutor: entry.aiTutor, contentFanout: entry.contentFanout } : null;
}

/**
 * Fetch GET /api/public/features and cache the result. Fail-closed: never
 * rejects; any error (network, timeout, non-2xx) resolves both flags to
 * `false`. Shared by both exports below — `fetchFlags` calls it un-deduped
 * (one network call per invocation); `isContentFanoutEnabled` wraps it with
 * the module-level in-flight guard.
 */
async function fetchAndCache(): Promise<FeatureFlags> {
  try {
    const { data } = await api.get('/api/public/features');
    const flags: FeatureFlags = {
      aiTutor: data?.ai_tutor === true,
      contentFanout: data?.content_fanout === true,
    };
    writeCache(flags);
    return flags;
  } catch {
    return FAIL_CLOSED;
  }
}

/**
 * Fetch GET /api/public/features and cache the result, for the
 * `useAiFeaturesEnabled` hook. Deliberately NOT deduplicated — every call
 * hits the network — so each component mount independently revalidates the
 * flags (matching this hook's pre-existing per-mount-fetch behavior; cache
 * is still consulted for the hook's synchronous initial paint via
 * `readCachedFlags`, just not by this function). Fail-closed: never rejects.
 */
export function fetchFlags(): Promise<FeatureFlags> {
  return fetchAndCache();
}

// Module-level in-flight promise, scoped to isContentFanoutEnabled ONLY:
// concurrent non-hook callers hitting a cold/expired cache share ONE network
// request instead of firing one each. Cleared once the request settles
// (success OR failure) so the next miss fetches fresh. Kept separate from
// the hook's `fetchFlags` above so a long-pending (or, in tests, deliberately
// never-resolving) hook fetch can never block a later getter call — or vice
// versa — behind the same stuck promise.
let inFlight: Promise<FeatureFlags> | null = null;

/**
 * isContentFanoutEnabled — plain async getter (non-hook) for call sites that
 * aren't React components, e.g. courseJourney.ts's content-create functions.
 * Cache-first: returns the cached `contentFanout` value without touching the
 * network when the cache is fresh; fetches on miss/expiry, deduplicated
 * across concurrent callers via the module-level in-flight promise above so
 * N concurrent calls on a cold cache produce exactly 1 network request.
 * Fail-closed: resolves `false`, never rejects.
 */
export async function isContentFanoutEnabled(): Promise<boolean> {
  const cached = readCachedFlags();
  if (cached) return cached.contentFanout;
  if (!inFlight) {
    inFlight = fetchAndCache().finally(() => {
      inFlight = null;
    });
  }
  const flags = await inFlight;
  return flags.contentFanout;
}
